import { CircleHelp } from "lucide-react";
import { useEffect, useRef } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";

import { Input, Label, TextField } from "@/components/field";
import { Tooltip, TooltipTrigger } from "@/components/tooltip";

import { useGetProjectDetailsOptions } from "../hooks/useGetProjectDetailsOptions";
import { useProjectDetails } from "../useProjectDetails";

export function SlotSettingsSection() {
  const { control, setValue } = useFormContext();
  const { isReadOnly, projectId } = useProjectDetails();
  const { data: options } = useGetProjectDetailsOptions(projectId);
  const products = options?.products || [];

  const selectedProductId = useWatch({ control, name: "productId" });

  const selectedProduct = products.find((p) => p.id === selectedProductId);
  const hasCredits =
    selectedProduct?.credits !== null && selectedProduct?.credits !== undefined;

  const previousProductIdRef = useRef<null | string | undefined>();

  useEffect(() => {
    if (products.length === 0) return;

    if (previousProductIdRef.current === undefined) {
      previousProductIdRef.current = selectedProductId ?? null;

      return;
    }

    if (previousProductIdRef.current === selectedProductId) return;

    previousProductIdRef.current = selectedProductId ?? null;

    if (!selectedProductId) return;

    const product = products.find((p) => p.id === selectedProductId);

    if (!product) return;

    const setOpts = { shouldDirty: true, shouldValidate: true };

    setValue(
      "slots.maxDesignSlots",
      product.slots?.maxDesignSlots ?? 0,
      setOpts,
    );
    setValue("slots.designCredit", product.credits ?? 0, setOpts);
  }, [selectedProductId, products, setValue]);

  return (
    <div className="bg-bg-secondary rounded-lg px-6 py-6 mx-6">
      <div className="flex items-center gap-2 mb-6">
        <h3 className="text-lg text-text-primary">Design Slot Settings</h3>
        <TooltipTrigger>
          <div className="text-text-tertiary cursor-help">
            <CircleHelp size={18} />
          </div>
          <Tooltip>
            The number of design requests being worked on simultaneously
          </Tooltip>
        </TooltipTrigger>
      </div>
      <div className="grid grid-cols-3 gap-x-6 gap-y-5">
        <Controller
          control={control}
          name="slots.maxDesignSlots"
          render={({ field, fieldState }) => (
            <TextField
              isDisabled={isReadOnly}
              isInvalid={!!fieldState.error}
              name={field.name}
              onBlur={field.onBlur}
              onChange={field.onChange}
              value={field.value}
            >
              <Label>Max. design slots</Label>
              <Input type="number" />
            </TextField>
          )}
        />
        {hasCredits && (
          <Controller
            control={control}
            name="slots.designCredit"
            render={({ field, fieldState }) => (
              <TextField
                isInvalid={!!fieldState.error}
                name={field.name}
                onBlur={field.onBlur}
                onChange={field.onChange}
                value={field.value}
              >
                <Label>Design credit</Label>
                <Input type="number" />
              </TextField>
            )}
          />
        )}
      </div>
    </div>
  );
}
